/**
 * The handful of line icons the reader chrome uses. Drawn at 24×24 in
 * currentColor, so a button's text colour is the icon's colour and the dark
 * and sepia themes need nothing of their own.
 */
function Icon({ d, className = "size-5" }: { d: string; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}

export const CloseIcon = (p: { className?: string }) => <Icon d="M6 6l12 12M18 6 6 18" {...p} />;

/** A flag: the small control that reports a problem (requirement 0122). */
export const FlagIcon = (p: { className?: string }) => (
  <Icon d="M5 21V4m0 0h11l-2 4 2 4H5" {...p} />
);

export const MenuIcon = (p: { className?: string }) => <Icon d="M4 7h16M4 12h16M4 17h16" {...p} />;

export const ChatIcon = (p: { className?: string }) => (
  <Icon
    d="M5 5.5A2.5 2.5 0 0 1 7.5 3h9A2.5 2.5 0 0 1 19 5.5v7a2.5 2.5 0 0 1-2.5 2.5H11l-4 4v-4h.5A2.5 2.5 0 0 1 5 12.5z"
    {...p}
  />
);

/**
 * BackIcon and ForwardIcon are chevrons that follow the interface's direction:
 * back points left in English and right in Persian, with no second icon and no
 * arrow written into each translation.
 */
export const BackIcon = ({ className = "size-4" }: { className?: string }) => (
  <Icon d="M15 5l-7 7 7 7" className={`${className} rtl:-scale-x-100`} />
);

export const ForwardIcon = ({ className = "size-4" }: { className?: string }) => (
  <Icon d="M9 5l7 7-7 7" className={`${className} rtl:-scale-x-100`} />
);

export const SendIcon = (p: { className?: string }) => <Icon d="M12 19V5M6 11l6-6 6 6" {...p} />;

/**
 * PlayIcon is filled, not drawn: a play button reads by its solid triangle. It
 * points right in Persian too — media controls do not mirror, as the player's
 * transport does not (Player.tsx).
 */
export const PlayIcon = ({ className = "size-5" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M8 5.6v12.8a1 1 0 0 0 1.52.85l10.4-6.4a1 1 0 0 0 0-1.7L9.52 4.75A1 1 0 0 0 8 5.6Z" />
  </svg>
);

export const PencilIcon = (p: { className?: string }) => (
  <Icon d="M4 20h4L19 9a2.8 2.8 0 0 0-4-4L4 16zM13.5 6.5l4 4" {...p} />
);

export const CheckIcon = (p: { className?: string }) => <Icon d="M5 12.5l4.5 4.5L19 7.5" {...p} />;

/** A Plus book this reader has no Plus for (ADR-0193). */
export const LockIcon = (p: { className?: string }) => (
  <Icon d="M7 11V8a5 5 0 0 1 10 0v3M5.5 11h13v9.5h-13Z" {...p} />
);

export const SettingsIcon = (p: { className?: string }) => (
  <Icon
    d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM19.4 13a7.5 7.5 0 0 0 0-2l2-1.6-2-3.4-2.4 1a7.4 7.4 0 0 0-1.7-1L15 3.5h-4l-.3 2.5a7.4 7.4 0 0 0-1.7 1l-2.4-1-2 3.4 2 1.6a7.5 7.5 0 0 0 0 2l-2 1.6 2 3.4 2.4-1a7.4 7.4 0 0 0 1.7 1l.3 2.5h4l.3-2.5a7.4 7.4 0 0 0 1.7-1l2.4 1 2-3.4Z"
    {...p}
  />
);

export const UsageIcon = (p: { className?: string }) => (
  <Icon d="M4 20V10M10 20V4M16 20v-7M22 20H2" {...p} />
);

/** My quotes: a pair of quotation marks. */
export const QuoteIcon = (p: { className?: string }) => (
  <Icon
    d="M10 7H6a1 1 0 0 0-1 1v4h4v1a3 3 0 0 1-3 3M19 7h-4a1 1 0 0 0-1 1v4h4v1a3 3 0 0 1-3 3"
    {...p}
  />
);

/** Share: an arrow leaving a tray, the platforms' own sign. */
export const ShareIcon = (p: { className?: string }) => (
  <Icon d="M12 15V3M8 7l4-4 4 4M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" {...p} />
);

export const ShieldIcon = (p: { className?: string }) => (
  <Icon d="M12 3l7 3v5c0 4.5-3 8.3-7 10-4-1.7-7-5.5-7-10V6l7-3Z" {...p} />
);

export const SignOutIcon = (p: { className?: string }) => (
  <Icon
    d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3M10 16l-4-4 4-4M6 12h10"
    className={`${p.className ?? "size-5"} rtl:-scale-x-100`}
  />
);

/**
 * LibraryIcon is the shelf toggle's mark: a plus while the book is not kept,
 * a check once it is. A bookmark read as "mark my place in this book", which
 * is what progress does; adding a book to a shelf is a plus, the way every
 * catalogue a reader already knows says it.
 *
 * Two shapes rather than one shape filled or not, so the state reads at tile
 * size across a room. Neither is a direction, so nothing mirrors in a
 * right-to-left layout.
 */
export const LibraryIcon = ({
  className = "size-5",
  added = false,
}: {
  className?: string;
  added?: boolean;
}) => <Icon d={added ? "M5 12.5l4.5 4.5L19 7.5" : "M12 5v14M5 12h14"} className={className} />;

/**
 * SparkleIcon is Ask's mark, the app's Modern Ask sparkle (auto_awesome): one
 * large four-pointed star and two small ones. Filled, so it reads at 18px.
 */
export const SparkleIcon = ({ className = "size-5" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M10 4.5l1.6 4.9 4.9 1.6-4.9 1.6L10 17.5l-1.6-4.9L3.5 11l4.9-1.6zM18 2l.8 2.2L21 5l-2.2.8L18 8l-.8-2.2L15 5l2.2-.8zM18 14l.8 2.2 2.2.8-2.2.8L18 20l-.8-2.2L15 17l2.2-.8z" />
  </svg>
);
